/* eslint-disable no-magic-numbers */
import { CircleLayer } from 'mapbox-gl';

import { SymbolLayerWithMapIcon } from '../../components/MapIcon';
import {
  ALL_FISHING_WATERS_TOGGLEABLE_GROUPS,
  EMPTY_FISHING_WATERS_ICON_SIZE,
  EMPTY_FISHING_WATERS_SIMPLIFIED_CIRCLE_RADIUS,
  EMPTY_FISHING_WATERS_SIMPLIFIED_MINZOOM,
  FISHING_WATERS_ICON_SIZE,
  FISHING_WATERS_MINZOOM,
  FISHING_WATERS_SIMPLIFIED_CIRCLE_RADIUS,
  FISHING_WATERS_SIMPLIFIED_MINZOOM,
} from '../../constants/mapLayers/fishingWaters';
import { LayerWithToggleableGroup } from '../../types/types';
import { SOURCE } from '../sources';

// FISHING WATERS
export const FISHING_WATERS_SIMPLIFIED_LAYER: LayerWithToggleableGroup & CircleLayer = {
  id: 'fishing_waters_simplified_layer',
  type: 'circle',
  source: SOURCE.fishingWaters.name,
  'source-layer': 'fishing_waters',
  minzoom: FISHING_WATERS_SIMPLIFIED_MINZOOM,
  paint: {
    'circle-color': '#3675B2',
    'circle-radius': FISHING_WATERS_SIMPLIFIED_CIRCLE_RADIUS,
    'circle-stroke-color': '#FFFFFF',
    'circle-stroke-width': 1,
  },
  filter: ['all', ['==', 'is_forbidden', false], ['!=', 'catches_counter', 0]],
  toggleableGroups: ['FISHING_WATERS'],
};

export const FISHING_WATERS_LAYER: LayerWithToggleableGroup & SymbolLayerWithMapIcon = {
  id: 'fishing_waters_layer',
  type: 'symbol',
  source: SOURCE.fishingWaters.name,
  'source-layer': 'fishing_waters',
  minzoom: FISHING_WATERS_MINZOOM,
  layout: {
    visibility: 'visible',
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-image': 'fishing_water_with_catches',
    'icon-size': FISHING_WATERS_ICON_SIZE,
  },
  filter: ['all', ['==', 'is_forbidden', false], ['!=', 'catches_counter', 0]],
  toggleableGroups: ['FISHING_WATERS'],
};

export const FISHING_WATERS_NEW_LAYER: LayerWithToggleableGroup & SymbolLayerWithMapIcon = {
  id: 'fishing_waters_new_bubble',
  type: 'symbol',
  source: SOURCE.fishingWaters.name,
  'source-layer': 'fishing_waters',
  minzoom: FISHING_WATERS_MINZOOM,
  filter: [
    'all',
    ['>', ['coalesce', ['get', 'activity_count'], ['get', 'activity_counter']], 0],
    ['!=', ['get', 'is_forbidden'], true],
  ],
  paint: {
    'text-color': '#FFFFFF',
  },
  layout: {
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-image': 'fishing_waters_new_bubble_large',
    'icon-text-fit': 'both',
    'icon-text-fit-padding': [8, 10, 8, 10],
    'text-allow-overlap': true,
    'text-anchor': 'top',
    'text-font': ['Roboto Medium'],
    'text-ignore-placement': true,
    'text-offset': [0, -4],
    'text-size': 10,
    'text-field': [
      'upcase',
      [
        'step',
        ['coalesce', ['get', 'activity_count'], ['get', 'activity_counter']],
        ['concat', ['coalesce', ['get', 'activity_count'], ['get', 'activity_counter']], ' New'],
        100,
        '99+ New',
      ],
    ],
    'text-letter-spacing': 0.03,
    visibility: 'none',
  },
  toggleableGroups: ALL_FISHING_WATERS_TOGGLEABLE_GROUPS,
};

export const FISHING_WATERS_HIGHLIGHT_LAYER: LayerWithToggleableGroup & SymbolLayerWithMapIcon = {
  id: 'fishing_waters_highlight_layer',
  type: 'symbol',
  source: SOURCE.fishingWaters.name,
  'source-layer': 'fishing_waters',
  minzoom: FISHING_WATERS_SIMPLIFIED_MINZOOM,
  layout: {
    visibility: 'visible',
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-image': 'highlight',
    'icon-size': ['step', ['zoom'], 0.7, 8, 0.7, 9, 0.7, 11, 0.8],
  },
  filter: ['==', 'external_id', ''],
  toggleableGroups: ALL_FISHING_WATERS_TOGGLEABLE_GROUPS,
};

// EMPTY FISHING WATER
export const EMPTY_FISHING_WATERS_SIMPLIFIED_LAYER: LayerWithToggleableGroup & CircleLayer = {
  id: 'empty_fishing_waters_simplified_layer',
  type: 'circle',
  source: SOURCE.fishingWaters.name,
  'source-layer': 'fishing_waters',
  minzoom: EMPTY_FISHING_WATERS_SIMPLIFIED_MINZOOM,
  paint: {
    'circle-color': '#8f8f8f',
    'circle-radius': EMPTY_FISHING_WATERS_SIMPLIFIED_CIRCLE_RADIUS,
    'circle-stroke-color': '#e3e3e3',
    'circle-stroke-width': 1,
  },
  filter: ['all', ['==', 'is_forbidden', false], ['==', 'catches_counter', 0]],
  toggleableGroups: ['EMPTY_FISHING_WATERS'],
};

export const EMPTY_FISHING_WATERS_LAYER: LayerWithToggleableGroup & SymbolLayerWithMapIcon = {
  id: 'empty_fishing_waters_layer',
  type: 'symbol',
  source: SOURCE.fishingWaters.name,
  'source-layer': 'fishing_waters',
  minzoom: 11,
  layout: {
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-image': 'fishing_water_without_catches',
    'icon-size': EMPTY_FISHING_WATERS_ICON_SIZE,
  },
  filter: ['all', ['==', 'is_forbidden', false], ['==', 'catches_counter', 0]],
  toggleableGroups: ['EMPTY_FISHING_WATERS'],
};

// FORBIDDEN FISHING WATER
export const FORBIDDEN_FISHING_WATERS_SIMPLIFIED_LAYER: LayerWithToggleableGroup & CircleLayer = {
  id: 'forbidden_fishing_waters_simplified_layer',
  type: 'circle',
  source: SOURCE.fishingWaters.name,
  'source-layer': 'fishing_waters',
  minzoom: EMPTY_FISHING_WATERS_SIMPLIFIED_MINZOOM,
  paint: {
    'circle-color': '#C12E4A',
    'circle-radius': EMPTY_FISHING_WATERS_SIMPLIFIED_CIRCLE_RADIUS,
    'circle-stroke-color': '#e3e3e3',
    'circle-stroke-width': 1,
  },
  filter: ['==', 'is_forbidden', true],
  toggleableGroups: ['FORBIDDEN_FISHING_WATERS'],
};

export const FORBIDDEN_FISHING_WATERS_LAYER: LayerWithToggleableGroup & SymbolLayerWithMapIcon = {
  id: 'forbidden_fishing_waters_layer',
  type: 'symbol',
  source: SOURCE.fishingWaters.name,
  'source-layer': 'fishing_waters',
  minzoom: 11,
  layout: {
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-image': 'forbidden_fishing_water',
    'icon-size': EMPTY_FISHING_WATERS_ICON_SIZE,
  },
  filter: ['==', 'is_forbidden', true],
  toggleableGroups: ['FORBIDDEN_FISHING_WATERS'],
};

// CATCH COUNTS
export const CATCH_COUNTS_LAYER: LayerWithToggleableGroup & SymbolLayerWithMapIcon = {
  id: 'catch_counts_layer',
  type: 'symbol',
  source: SOURCE.fishingWaters.name,
  'source-layer': 'fishing_waters',
  minzoom: 11,
  paint: {
    'text-color': '#FFFFFF',
  },
  layout: {
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-image': 'catch_count_background_trench',
    'icon-text-fit': 'both',
    'icon-text-fit-padding': [5, 10, 2, 9],
    'text-allow-overlap': true,
    'text-anchor': 'top',
    'text-font': ['Roboto Medium'],
    'text-ignore-placement': true,
    'text-offset': [1.2, -1.8],
    'text-size': 10,
    'text-field': ['get', 'catches_counter'],
  },
  filter: ['all', ['==', 'is_forbidden', false], ['!=', 'catches_counter', 0]],
  toggleableGroups: ['FISHING_WATERS'],
};
